import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { trackEvent } from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileCTA from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileCTA'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTileHeaderArea from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileHeaderArea'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import { AccommodationSameRoomOptionTileSubgrid } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileSubgrid'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import AgentExclusivePackageLabel from 'components/Common/Labels/AgentExclusivePackageLabel'
import Clickable from 'components/Common/Clickable'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import PayLaterPill from 'components/Common/PayLaterPill'
import type { PriceDetailsModalOfferProps } from 'components/Common/PriceDetails/PriceDetailsModal'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PackageInclusionsValidityText, { hasPackageInclusionsValidityText } from 'components/OfferPage/Accommodation/PackageInclusionsValidityText'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { Experiments, OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { SOURCE_TYPES } from 'constants/source'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import RateImpressionContext from 'contexts/RateImpressionContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getPopularForOccupantsBannerMessage } from 'hooks/OfferPage/useSuggestedPackageOptionFromTotalSales'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import OfferPageBurnPriceDisplay from 'components/OfferPage/Loyalty/OfferPageBurnPriceDisplay'
import { isOfferPageFullyPaidWithPoints } from 'lib/offer/isOfferPageFullyPaidWithPoints'
import OfferPagePaidEntirelyWithPointsLabel from 'components/OfferPage/Loyalty/OfferPagePaidEntirelyWithPointsLabel'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { EmptyArray } from 'lib/array/arrayUtils'
import RateTierProgressionCallOutBanner from 'loyalty/components/Banners/RateTierProgressionCallOutBanner'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useHotelTierProgressionEligibility from 'loyalty/hooks/useHotelTierProgressionEligibility'
import usePropertyRateStatusCreditsCalculation from 'loyalty/hooks/usePropertyRateStatusCreditsCalculation'
import {
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { getPropertyCheapestHotelOnlyRateForDiscount, getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'
import { getSource } from 'selectors/offerSelectors'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'

import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyPayAtPropertyModal from './PropertyPayAtPropertyModal'
import PropertyPromotionDetails from './PropertyPromotionDetails'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'
import { propertySingleRoomBookNowOrAddToCartCallback } from './propertyBuyCallback'
import { getPayAtPropertyDetails } from './singlePropertyUtils'

const SuggestedPackageBannerContainer = styled.div`
  ${mediaQueryUp.tablet} {
    border-inline: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const RefundabilityInclusionsSpacing = styled.div`
  padding-top: ${rem(8)};
`

function RefundabilityInclusionsGap(props: { enabled: boolean, children: React.ReactNode }) {
  const { enabled, children } = props

  if (!enabled) {
    return <>{children}</>
  }

  return <RefundabilityInclusionsSpacing>{children}</RefundabilityInclusionsSpacing>
}

interface Props {
  'data-testid'?: string
  property: App.Property.Property
  isPropertyUltraLux: boolean
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer | undefined
  rate: App.EnrichedPropertyRate
  originAirport: App.Airport | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isLoggedIn: boolean
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
  canShowMargin: boolean
  canShowStaffLabel: boolean
  lpcRatesUpliftExperimentVariation: string | undefined
  isInclusionsValidityEnabled: boolean
  cheapestHotelOnlyDiscountRate: App.EnrichedPropertyRate | undefined
  // TODO analytics
  roomListId: string
  roomPosition: number // TODO one-indexed
  rateListId: string
  ratePosition: number
  /** Hide the tier progression banner (used when rendered inside PropertyRateOptionMerged) */
  hideTierProgressionBanner?: boolean
  cancellationPolicyContentOverride?: React.ReactNode
}

function PropertyRateOption(props: Props) {
  const {
    property,
    isPropertyUltraLux,
    lpcOffer,
    rate,
    originAirport,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    source,
    utmContent,
    roomOption,
    isLoggedIn,
    isSpoofed,
    subscriber,
    canShowMargin,
    canShowStaffLabel,
    lpcRatesUpliftExperimentVariation,
    isInclusionsValidityEnabled,
    cheapestHotelOnlyDiscountRate,
    roomListId,
    roomPosition,
    rateListId,
    ratePosition,
    hideTierProgressionBanner,
    cancellationPolicyContentOverride,
  } = props

  const loyaltyProductType: App.LoyaltyAccommodationProductType = rate.service.type === 'bedbank' ? 'hotel_lpp_eps' : 'hotel_lpc'
  const burnResult = useOfferPageBurnPoints(rate.totals.displayPrice, loyaltyProductType)
  const { enabled: showPointsPrice, pointsToBurn: burnPointsToApply, priceInPoints: burnPriceInPoints, hideZeroCash: burnHideZeroCash } = burnResult
  const intl = useIntl()
  const showPointsSavings = isOfferPageFullyPaidWithPoints(
    burnPointsToApply,
    burnPriceInPoints,
    burnHideZeroCash,
  )
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent: trackOfferPageEvent } = useContext(OfferPageStateTrackingContext)
  const impressionContext = useContext(RateImpressionContext)
  const hasAvailableFlights = rate.includesFlights && typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0
  const metasearchPriceId = useMetasearchPriceId()
  const cheapestHotelOnlyRate = cheapestHotelOnlyDiscountRate
  const isMetasearchDiscountEligibleRate = (
    rate.service.type === 'bedbank' &&
    !rate.includesFlights &&
    cheapestHotelOnlyRate &&
    'service' in cheapestHotelOnlyRate &&
    cheapestHotelOnlyRate.service.type === 'bedbank' &&
    cheapestHotelOnlyRate.service.propertyId === rate.service.propertyId &&
    cheapestHotelOnlyRate.rateId === rate.rateId
  )
  const [discountAmount, discountFetching] = useMetasearchDiscount(rate.totals.displayPrice, {
    propertyId: rate.service.type === 'bedbank' ? rate.service.propertyId : undefined,
    checkIn,
    checkOut,
    rooms,
    disabled: !isMetasearchDiscountEligibleRate,
  })
  const displayPrice = Math.max(rate.totals.displayPrice - discountAmount, 0)
  const displayMemberPrice = rate.luxPlusTotals?.displayPrice ? Math.max(rate.luxPlusTotals.displayPrice - discountAmount, 0) : undefined

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    if (!isTasteOfGoldActive || !lpcOffer) return false
    return isTasteOfGoldEligibleOffer(
      lpcOffer,
      rate.currency,
      property.location.countryCode?.toLowerCase() !== 'au',
      rate.totals.sellingPrice,
    )
  }, [isTasteOfGoldActive, lpcOffer, rate.currency, rate.totals.sellingPrice, property.location.countryCode])

  const impressionKey = useMemo(() => {
    if (hasAvailableFlights && originAirport?.code) {
      return `${rate.rateId}:flight-bundle:${originAirport.code}`
    }
    return `${rate.rateId}:hotel`
  }, [rate.rateId, hasAvailableFlights, originAirport?.code])

  const canFireImpression = !!(checkIn && checkOut && roomListId && roomPosition && rateListId)

  const [inViewRef, inView] = useInView({ skip: impressionContext.hasFired(impressionKey), threshold: 0.5 })

  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined
  const rawBedbankDiscount = useBedbankDiscount(rate.totals.discount, rate.luxPlusTotals?.discount ?? 0)
  // TODO(HP-1404): Show margin details for LuxPlus+ prices.
  const shouldShowMargin = !!(canShowMargin && rate.totals.margin && rate.totals.marginAud)
  const payAtPropertyDetails = getPayAtPropertyDetails(rate)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode: property.location.countryCode,
        propertyState: property.location.region,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
    ]
  }, [rate, property.location.countryCode, property.location.region, regionCode, isPropertyUltraLux])

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const currentCurrency = useAppSelector(state => state.geo.currentCurrency)
  const airports = useAppSelector(state => state.geo.airports)
  const memberId = useAppSelector(state => state.auth.account.memberId)

  const { statusCredits: rateStatusCredits, isLoading: statusCreditsLoading } = usePropertyRateStatusCreditsCalculation({
    rate,
    propertyCountryCode: property.location.countryCode,
    propertyState: property.location.region,
    currentRegionCode: regionCode,
    isUltraLux: isPropertyUltraLux,
    nights: duration,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount,
  })

  const tierProgressionEligibility = useHotelTierProgressionEligibility({
    statusCredits: rateStatusCredits,
    nights: duration,
    checkoutDate: checkOut,
    skip: !isLoyaltyEnabled || !hasLoyaltyAccount || statusCreditsLoading,
  })

  const { showTierProgressionBanner: rawShowTierProgressionBanner, highestUnlockableTier, highestUnlockableTierName } = tierProgressionEligibility
  const canActivateTierProgressionExperiment = rawShowTierProgressionBanner && !tierProgressionEligibility.isLoading
  const { isAnyVariant: isTierProgressionExperimentVariant } = useExperiment(
    Experiments.croTierProgressionOfferPage,
    { canActivate: canActivateTierProgressionExperiment },
  )
  const isTierProgressionExperimentEnabled = canActivateTierProgressionExperiment && isTierProgressionExperimentVariant
  const showTierProgressionBanner = rawShowTierProgressionBanner && !hideTierProgressionBanner && isTierProgressionExperimentEnabled

  // These hook calls are purely to declaratively activate experiments. Imperative activation within
  // the rate impressions `useEffect` below would be delayed until this component is re-rendered.
  const isRateFreshlyInView = inView && !impressionContext.hasFired(impressionKey)
  useExperiment(Experiments.sppUpliftLpcRates, {
    canActivate:
      // Rate is in view for the first time, and…
      isRateFreshlyInView &&
      (
        // … either: (i) we're in the control and showing an LPC rate uplifted for inclusions; …
        (lpcRatesUpliftExperimentVariation === 'control' && rate.calloutReason === 'recommended') ||
        // … (ii) we're in variant A (i.e. no LPC rate uplifts); or…
        (lpcRatesUpliftExperimentVariation === 'variant_a') ||
        // … (iii) we're in variant B and showing an LPC rate uplifted for popularity.
        (lpcRatesUpliftExperimentVariation === 'variant_b' && rate.calloutReason === 'popular-occupancy')
      ),
  })

  useEffect(() => {
    if (!canFireImpression || !inView) return
    if (impressionContext.hasFired(impressionKey)) return

    trackEvent(propertyRateImpressionEventWithContext({
      occupancy: rooms,
      checkIn,
      checkOut,
      roomListId,
      roomPosition,
      rateListId,
      ratePosition,
      rate,
    }))
    impressionContext.markAsFired(impressionKey)
  }, [
    canFireImpression,
    checkIn,
    checkOut,
    impressionContext,
    impressionKey,
    inView,
    ratePosition,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
  ])

  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren’t set')
    trackOfferPageEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    const chosenRate = (payAtPropertyDetails.shouldShowPill && payAtPropertyDetails.shouldShowModal)
      ? await showModal<App.EnrichedPropertyRate | undefined>(
          <PropertyPayAtPropertyModal
            propertyTimeZone={property.location.timezone}
            duration={duration}
            propertyCollectRate={payAtPropertyDetails.propertyCollectRate}
            leCollectRate={payAtPropertyDetails.leCollectRate}
          />,
        )
      : rate
    if (!chosenRate) return

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      currentCurrency,
      airports,
      currentRegionCode: regionCode,
      memberId,
      offerPageDispatch,
      modalElementContext,
      showModal,
      intl,
      property,
      rate: chosenRate,
      ratePosition,
      originAirportCode: originAirport?.code,
      checkIn,
      checkOut,
      duration,
      rooms,
      regionCode,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      subscriber,
      roomListId,
      roomPosition,
      rateListId,
      metasearchPriceId,
      shouldApplyDiscount: isMetasearchDiscountEligibleRate,
    })
  }, [
    airports,
    checkIn,
    checkOut,
    currentCurrency,
    dispatch,
    duration,
    intl,
    isLoggedIn,
    isSpoofed,
    memberId,
    modalElementContext,
    metasearchPriceId,
    offerPageDispatch,
    originAirport?.code,
    payAtPropertyDetails,
    property,
    rate,
    rateListId,
    ratePosition,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    isMetasearchDiscountEligibleRate,
    showModal,
    source,
    subscriber,
    trackOfferPageEvent,
    utmContent,
  ])

  const handleBookNowFromModal = useCallback(() => {
    onCtaClick(false)
  }, [onCtaClick])

  const handleAddToCartFromModal = useCallback(() => {
    onCtaClick(true)
  }, [onCtaClick])

  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])
  const ctas = useMemo(() => <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      includesFlights={rate.includesFlights}
      onCtaClick={onCtaClick}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>, [onCtaClick, onToggleFilter, rate])

  const cancellationPolicy = <PropertyCancellationPolicy
    propertyTimeZone={property.location.timezone}
    headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={rate.fullyRefundableUntil}
    cancellationPolicies={rate.cancellationPolicies}
    currency={rate.currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  const openPriceDetailsModal = useCallback(async() => {
    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = rate.service.type === 'lpc' ? {
      offerId: rate.service.offerId,
      offerType: OFFER_TYPE_ALWAYS_ON,
      saleUnit: 'room',
      displayPricingAsPerPerson: false,
    } : {
      offerId: rate.service.propertyId,
      offerType: OFFER_TYPE_BED_BANK,
      saleUnit: 'room',
      // TODO(HP-1614): Handle `bedbankOffer.displayPricingAsPerPerson`.
      displayPricingAsPerPerson: false,
    }
    const dueAtPropertyMessage = rate.service.type === 'lpc' ? property.taxesAndFeesContent : undefined
    // NB: This is used to narrow down the correct Agent Hub commission. However, it's unclear why
    // we don't do the same with package IDs for LPC rates.
    const rateId = rate.service.type === 'bedbank' ? rate.service.rateId : undefined

    // This implementation is based off `<OfferPriceDetailsRow/>`.
    // NB: Don't pass `flightPrice` to `<PriceDetailsModal/>`, as it's already baked in.
    const result = await showModal<'fine-print' | undefined>(<PriceDetailsModal
      {...priceDetailsModalOfferProps}
      offerPage
      duration={duration}
      taxesAndFees={rate.totals.taxesAndFees}
      propertyFees={rate.totals.propertyFees}
      hotelPrice={rate.totals.sellingPrice}
      hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
      rooms={rooms}
      dueAtPropertyMessage={dueAtPropertyMessage}
      cta={
        <Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
          {ctas}
        </Group>
      }
      rateId={rateId}
      postTaxDiscount={discountAmount || undefined}
    />)
    if (result === 'fine-print') {
      window.location.href = '#fine-print'
    }
  }, [ctas, discountAmount, duration, property, rate, rooms, showModal])
  const taxesAndFeesContent = <Clickable onClick={openPriceDetailsModal}>
    <Caption variant="medium" colour="light-foreground-alt" underline>
      {intl.formatMessage({
        id: 'H29_x-SS95OszUi2OiUM',
        defaultMessage: 'Includes taxes & fees',
        description: 'Label indicating that taxes and fees are included in the price',
      })}
    </Caption>
  </Clickable>

  const noFlightsAvailableMessage = intl.formatMessage(
    {
      id: 'KCe0399qOgmK-1GZSQxq',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: originAirport?.name },
  )
  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'WwEhobNYMcqSErYgfeMM',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
    description: 'Error message when departure is too soon for online flight booking',
  })
  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: '0j8ptAKnepQILSZ4qfTs',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when departure date is too far in advance for flight booking',
  })
  const addFlightsMessage = intl.formatMessage({
    id: 'OrJCJ4isDFtPDzbv6XYB',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to the hotel package at checkout',
  })

  const strongCalloutMessage = useMemo(() => {
    switch (rate.calloutReason) {
      case 'popular-occupancy':
        const message = getPopularForOccupantsBannerMessage(intl, rooms)
        if (message) return message
        // fall through
      case 'recommended':
        return intl.formatMessage({
          id: 'IYB1pLJaKfNI5CSqWM8f',
          defaultMessage: 'Recommended package',
          description: 'Promotional banner message shown above an uplifted rate',
        })
    }
  }, [intl, rate.calloutReason, rooms])
  const staffRateLabel = canShowStaffLabel && rate.staffLabel ? intl.formatMessage({
    id: 'HJnIuZ3JW8_X7JmL0chU',
    defaultMessage: '{staffLabel} (label only shows for staff)',
    description: 'Staff-only label providing additional rate details',
  }, { staffLabel: rate.staffLabel }) : undefined
  const hasRefundabilitySelector = !!cancellationPolicyContentOverride
  const { isAnyVariant: isRefundPolicyRadioBelowInclusions } = useExperiment(
    Experiments.croRefundPolicyRadioBtnsBelowInclusions,
    { canActivate: hasRefundabilitySelector },
  )
  const shouldShowRefundPolicyRadioBelowInclusions = hasRefundabilitySelector && isRefundPolicyRadioBelowInclusions

  /**
   * LPC RATES
   */
  if (rate.service.type === 'lpc') {
    const shouldShowLimitedTimeSpecialLabel = rate.service.isTactical || rate.hasAppliedDiscount || !!rate.promotion
    const lpcDiscount = rate.totals.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.totals.discount : 0
    const shouldShowValue = rate.totals.valuedUpToPrice > rate.totals.sellingPrice
    const packageId = rate.service.packageId
    const matchingPackage = lpcOffer?.packages.find(pkg =>
      pkg.id === packageId || pkg.lePackageId === packageId,
    )
    const includedGuests = matchingPackage?.roomRate.includedGuests ?? EmptyArray
    const selectedOccupancy = rooms.length === 1 ? rooms[0] : undefined
    const hasPackageInclusions = (
      rate.luxPlusInclusions.length > 0 ||
      rate.bonusInclusions.length > 0 ||
      rate.bundleInclusions.length > 0 ||
      rate.inclusions.length > 0
    )
    const shouldShowInclusionsValidityText = isInclusionsValidityEnabled && hasPackageInclusions && hasPackageInclusionsValidityText(includedGuests, matchingPackage?.roomPolicyDescription)
    const showAgentExclusivePackageLabel = (
      !!matchingPackage?.isAgentHubExclusive &&
      !lpcOffer?.isAgentHubExclusive &&
      !property.isAgentHubExclusive
    )

    const lpcDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'WlUcNFNb6HRC0YOSv57_',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for LPC property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '-mSccfdQDlul78PcyBog',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Duration in nights for LPC property rate option on the form',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTileSubgrid>
        {strongCalloutMessage && <SuggestedPackageBannerContainer>
          <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>
            {strongCalloutMessage}
          </MessageBanner>
        </SuggestedPackageBannerContainer>}
        <AccommodationSameRoomOptionTile data-testid={rate.service.rateId} ref={inViewRef}>
          {showTierProgressionBanner && highestUnlockableTier && (
            <AccommodationSameRoomOptionTileHeaderArea>
              <RateTierProgressionCallOutBanner
                highestUnlockableTier={highestUnlockableTier}
                highestUnlockableTierName={highestUnlockableTierName}
                onBookNow={handleBookNowFromModal}
                onAddToCart={handleAddToCartFromModal}
                checkoutDate={checkOut}
              />
            </AccommodationSameRoomOptionTileHeaderArea>
          )}
          <AccommodationSameRoomOptionTileDetails
            optionName={rate.name}
            tagLineLabel={<>
              {shouldShowLimitedTimeSpecialLabel && <ProductTypeLabel productType="limited_time_special" />}
            </>}
          >
            {!shouldShowRefundPolicyRadioBelowInclusions && (cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {cancellationPolicy}
              {staffRateLabel && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {staffRateLabel}
                </BodyText>
              )}
            </Group>)}
            {rate.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
            {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
            <RefundabilityInclusionsGap enabled={hasRefundabilitySelector && !shouldShowRefundPolicyRadioBelowInclusions}>
              <LargeContentToggle
                maxHeight={208}
                showMoreText={intl.formatMessage({
                  id: '74uMot_LhpmF_fW845tr',
                  defaultMessage: 'View more inclusions',
                  description: 'Toggle text to expand inclusions list for property rate option',
                })}
                showLessText={intl.formatMessage({
                  id: 'i_MD93nsLRPDKVjXbxqE',
                  defaultMessage: 'View less inclusions',
                  description: 'Toggle text to collapse inclusions list for property rate option',
                })}
              >
                <VerticalSpacer gap={12}>
                  {rate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
                    luxPlusInclusions={rate.luxPlusInclusions}
                    accommodationOffer={lpcOffer}
                  />}
                  {rate.bonusInclusions.length > 0 && <OfferPagePackageInclusionsList variant="bonus">
                    {rate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                  </OfferPagePackageInclusionsList>}
                  <Group direction="vertical" gap={4}>
                    {rate.bundleInclusions.length > 0 && <VerticalSpacer gap={4}>
                      {rate.bundleInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} isHighlighted colour="exclusive" />)}
                    </VerticalSpacer>}
                    {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
                      {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                    </VerticalSpacer>}
                    <LoyaltyInclusions offer={lpcOffer} variant="offer-page" />
                  </Group>
                </VerticalSpacer>
              </LargeContentToggle>
              {shouldShowInclusionsValidityText && (
                <PackageInclusionsValidityText includedGuests={includedGuests} policyDescription={matchingPackage?.roomPolicyDescription} selectedOccupancy={selectedOccupancy} />
              )}
            </RefundabilityInclusionsGap>
            {shouldShowRefundPolicyRadioBelowInclusions && (
              <RefundabilityInclusionsGap enabled>
                {cancellationPolicyContentOverride}
              </RefundabilityInclusionsGap>
            )}
          </AccommodationSameRoomOptionTileDetails>

          <AccommodationSameRoomOptionTilePricing>
            {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
              {lpcDiscount > 0 && <PriceRowValueDiscount
                type="estimate"
                discountPercentage={lpcDiscount}
              />}
              {!!rate.paymentScheduleInfo && <PayLaterPill />}
              {payAtPropertyDetails.shouldShowPill && <PayAtPropertyPill hasMultipleOptions={payAtPropertyDetails.shouldShowModal} />}
            </Group>}
            {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
            {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
              {showAgentExclusivePackageLabel && <LabelGroup>
                <AgentExclusivePackageLabel />
              </LabelGroup>}
              <div>
                {showPointsSavings && <OfferPagePaidEntirelyWithPointsLabel />}
                <Caption variant="medium">
                  <Group direction="vertical" gap={4}>
                    <div>
                      <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                        {lpcDurationLabel}
                      </PriceRowPriceCaption>
                      {showPointsPrice && <OfferPageBurnPriceDisplay
                        totalPrice={rate.totals.displayPrice}
                        pointsToApply={burnPointsToApply}
                        priceInPoints={burnPriceInPoints}
                        pointsHideZeroCash={burnHideZeroCash}
                        memberPrice={rate.luxPlusTotals?.displayPrice}
                        size="M"
                        saleUnit="total"
                      />}
                      {!showPointsPrice && <LuxPlusPriceGuard
                        showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                        fallbackPrice={<PriceRowPrice
                          data-testid="property-package-price"
                          size="M"
                          price={rate.totals.displayPrice}
                          saleUnit="total"
                        />}
                      >
                        <LuxPlusPriceStack
                          size="M"
                          testid="property-package-price"
                          price={rate.totals.displayPrice}
                          memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                          saleUnit="total"
                        />
                      </LuxPlusPriceGuard>}
                      {shouldShowValue && <PriceRowValueDiscountWithCaption
                        size="M"
                        originalValue={rate.totals.valuedUpToPrice}
                      />}
                      {taxesAndFeesContent}
                    </div>
                    {rate.exceedsIncludedGuests && <AdditionalGuestsPopup
                      complex={false}
                      description={intl.formatMessage({
                        id: '3KcTGG2Q7zKoXLtwouAC',
                        defaultMessage: '+ Extra guest surcharge',
                        description: 'Description for additional guests popup in property rate option',
                      })}
                      modalContent={rate.includedGuestsExceededPolicy}
                    />}
                    {shouldShowMargin && <PriceRowMarginText
                      marginValue={rate.totals.marginAud!}
                      margin={rate.totals.margin}
                      type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                    />}
                  </Group>
                </Caption>
              </div>
              <LoyaltyPointsEarnWithBreakdownModal
                calculationRequests={pointsEarnCalculationRequests}
                calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                pointsToApply={burnPointsToApply}
                burnPriceInPoints={burnPriceInPoints}
              />
            </VerticalSpacer>}
            {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
              {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="light-foreground-alt" variant="small">
                {noFlightsAvailableMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsWithin24HoursMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="light-foreground-alt" variant="small">
                {flightsAdvanceBookingMessage}
              </BodyText>}
              {rate.cheapestFlightPrice === 0 && <BodyText colour="light-foreground-alt" variant="small">
                {addFlightsMessage}
              </BodyText>}
            </VerticalSpacer>}
            {/*
              * NB: Because we render a "plain" commission rate with a well-defined price, we don't
              * need to worry about aggregating min/max commission rates across all active offers
              * linked to this property.
              */}
            <PriceRowAgentHubCommission
              productType="tactical_ao_hotel"
              offerId={rate.service.offerId}
              packageId={rate.service.packageId}
              offerTotal={rate.totals.sellingPrice}
              kind="plain"
            />
          </AccommodationSameRoomOptionTilePricing>

          <AccommodationSameRoomOptionTileCTA>
            {ctas}
          </AccommodationSameRoomOptionTileCTA>
        </AccommodationSameRoomOptionTile>
      </AccommodationSameRoomOptionTileSubgrid>
    )
  }

  /**
   * BEDBANK RATES
   */
  if (rate.service.type === 'bedbank') {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0

    const bedbankDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'qkFlsC5yUKDoPpbkWOvn',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for bedbank property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '00Dl9U8_kbrsX9iSc4kT',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights for bedbank property rate option',
          },
          { count: duration },
        )

    return (
      <AccommodationSameRoomOptionTileSubgrid>
        {strongCalloutMessage && <SuggestedPackageBannerContainer>
          <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>
            {strongCalloutMessage}
          </MessageBanner>
        </SuggestedPackageBannerContainer>}
        <AccommodationSameRoomOptionTile ref={inViewRef}>
          {showTierProgressionBanner && highestUnlockableTier && (
            <AccommodationSameRoomOptionTileHeaderArea>
              <RateTierProgressionCallOutBanner
                highestUnlockableTier={highestUnlockableTier}
                highestUnlockableTierName={highestUnlockableTierName}
                onBookNow={handleBookNowFromModal}
                onAddToCart={handleAddToCartFromModal}
                checkoutDate={checkOut}
              />
            </AccommodationSameRoomOptionTileHeaderArea>
          )}
          <AccommodationSameRoomOptionTileDetails
            optionName={rate.name}
            tagLineLabel={<>
              {rate.promotion && <ProductTypeLabel productType="limited_time_special" />}
            </>}
          >
            {!shouldShowRefundPolicyRadioBelowInclusions && (cancellationPolicyContentOverride || <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {cancellationPolicy}
              {staffRateLabel && (
                <BodyText variant="medium" colour="light-foreground-alt">
                  {staffRateLabel}
                </BodyText>
              )}
            </Group>)}
            {rate.promotion && <PropertyPromotionDetails promotion={rate.promotion} />}
            {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
            <RefundabilityInclusionsGap enabled={hasRefundabilitySelector && !shouldShowRefundPolicyRadioBelowInclusions}>
              <Group direction="vertical" gap={4}>
                {rate.bundleInclusions.length > 0 && (
                  <VerticalSpacer gap={4}>
                    {rate.bundleInclusions.map(inclusion => (
                      <OfferInclusionItem key={inclusion.id} inclusion={inclusion} isHighlighted colour="exclusive" />
                    ))}
                  </VerticalSpacer>
                )}
                {rate.inclusions.length > 0 && (
                  <VerticalSpacer gap={4}>
                    {rate.inclusions.map(inclusion => (
                      <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />
                    ))}
                  </VerticalSpacer>
                )}
                <LoyaltyInclusions offer={lpcOffer} variant="offer-page" />
              </Group>
            </RefundabilityInclusionsGap>
            {shouldShowRefundPolicyRadioBelowInclusions && (
              <RefundabilityInclusionsGap enabled>
                {cancellationPolicyContentOverride}
              </RefundabilityInclusionsGap>
            )}
          </AccommodationSameRoomOptionTileDetails>

          <AccommodationSameRoomOptionTilePricing>
            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
                {bedbankDiscount > 0 && (
                  <PriceRowValueDiscount
                    type="estimate"
                    discountPercentage={bedbankDiscount}
                  />
                )}
                {!!rate.paymentScheduleInfo && <PayLaterPill />}
                {payAtPropertyDetails.shouldShowPill && <PayAtPropertyPill hasMultipleOptions={payAtPropertyDetails.shouldShowModal} />}
              </Group>
            )}

            {rate.includesFlights &&
              rate.cheapestFlightPrice === 'loading' && (
              <LoadingIndicator floating opaque />
            )}

            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <VerticalSpacer gap={12}>
                <div>
                  {showPointsSavings && <OfferPagePaidEntirelyWithPointsLabel />}
                  <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                    {bedbankDurationLabel}
                  </PriceRowPriceCaption>
                  {showPointsPrice && <OfferPageBurnPriceDisplay
                    totalPrice={displayPrice}
                    pointsToApply={burnPointsToApply}
                    priceInPoints={burnPriceInPoints}
                    pointsHideZeroCash={burnHideZeroCash}
                    memberPrice={displayMemberPrice}
                    size="M"
                    saleUnit="total"
                  />}
                  {!showPointsPrice && <LuxPlusPriceGuard
                    showMemberPrice={
                      (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                    }
                    fallbackPrice={
                      <PriceRowPrice
                        data-testid="property-package-price"
                        size="M"
                        price={displayPrice}
                        saleUnit="total"
                      />
                    }
                  >
                    <LuxPlusPriceStack
                      size="M"
                      testid="property-package-price"
                      price={displayPrice}
                      memberPrice={displayMemberPrice ?? 0}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>}
                  <LoadingIndicator visible={discountFetching} floating opaque />

                  {!!rawBedbankDiscount && (
                    <PriceRowValueDiscountWithCaption
                      size="M"
                      originalValue={rate.totals.valuedUpToPrice}
                    />
                  )}

                  {taxesAndFeesContent}

                  {shouldShowMargin && (
                    <PriceRowMarginText
                      marginValue={rate.totals.marginAud!}
                      margin={rate.totals.margin}
                      supplier={rate.service.supplier}
                      type={
                        rate.includesFlights
                          ? 'property_estimate_accom'
                          : 'property_estimate'
                      }
                    />
                  )}
                </div>

                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={
                    rate.includesFlights ? 'estimate' : 'exact'
                  }
                  pointsToApply={burnPointsToApply}
                  burnPriceInPoints={burnPriceInPoints}
                />
              </VerticalSpacer>
            )}

            {rate.includesFlights &&
              (typeof rate.cheapestFlightPrice === 'string' ||
                rate.cheapestFlightPrice === 0) && (
              <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                  rate.cheapestFlightPrice === 'unavailable') && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {noFlightsAvailableMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooEarly' && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {flightsWithin24HoursMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooLate' && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {flightsAdvanceBookingMessage}
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 0 && (
                  <BodyText colour="light-foreground-alt" variant="small">
                    {addFlightsMessage}
                  </BodyText>
                )}
              </VerticalSpacer>
            )}
            {/*
              * NB: Because we render a "plain" commission rate with a well-defined price, we don't
              * need to worry about aggregating min/max commission rates across all active offers
              * linked to this property.
              */}
            <PriceRowAgentHubCommission
              productType="bedbank_hotel"
              offerId={rate.service.propertyId}
              packageId={rate.service.rateId}
              offerTotal={rate.totals.sellingPrice}
              kind="plain"
            />
          </AccommodationSameRoomOptionTilePricing>

          <AccommodationSameRoomOptionTileCTA>
            {ctas}
          </AccommodationSameRoomOptionTileCTA>
        </AccommodationSameRoomOptionTile>
      </AccommodationSameRoomOptionTileSubgrid>
    )
  }

  return null
}

export default withOfferPageAndReduxState((state, appState) => {
  const propertyOfferMapping = getPropertyOfferMapping(state, appState)
  return {
    property: state.property!,
    isPropertyUltraLux: propertyOfferMapping?.isUltraLux ?? false,
    originAirport: state.airport,
    checkIn: state.checkInDate,
    checkOut: state.checkOutDate,
    duration: state.duration,
    rooms: state.rooms,
    regionCode: appState.geo.currentRegionCode,
    source: getSource(appState),
    utmContent: appState.router.location.query.utm_content,
    roomOption: state.roomOption,
    isLoggedIn: selectLoggedIn(appState),
    isSpoofed: appState.auth.account.isSpoofed,
    subscriber: getEnquirySubscriber(appState),
    canShowMargin: appState.auth.account.isSpoofed && appState.system.showMarginInSpoofMode,
    canShowStaffLabel: isLEStaff(appState) || appState.auth.account.isSpoofed,
    // For Reasons™, `getOptimizelyExperimentVariation` returns `undefined` for the `'control'`
    // variation, so we'll have to directly read from state.
    lpcRatesUpliftExperimentVariation: appState.optimizely.optimizelyExperiments[OptimizelyExperiments.sppUpliftLpcRates]?.variationKey,
    isInclusionsValidityEnabled: !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.croInclusionValidity],
    cheapestHotelOnlyDiscountRate: getPropertyCheapestHotelOnlyRateForDiscount(state, appState),
    open: !!state.activeFilter,
  }
}, PropertyRateOption, preventRenderWithOpenFilter)
